// Upgrade Medic Configuration
// Add new resource types here - the UI will automatically adapt

import { IconProps } from '@amzn/awsui-components-core';

// ============================================
// INFRASTRUCTURE CONFIGURATION
// ============================================

export interface UpgradeMedicStageConfig {
  accountId: string;
  region: string;
  roleArn: string;
  tables: {
    resourceInventory: string;
    jobRuns: string;
    versionSetDetails: string;
  };
  cloudWatch: {
    logGroupName: string;
  };
}

const STAGE_CONFIGS: Record<string, UpgradeMedicStageConfig> = {
  alpha: {
    accountId: '075758923504',
    region: 'us-west-2',
    roleArn: 'arn:aws:iam::075758923504:role/upgrade-medic-ui-role-alpha',
    tables: {
      resourceInventory: 'verscender-resource-inventory-alpha',
      jobRuns: 'verscender-job-runs-alpha',
      versionSetDetails: 'verscender-versionset-details-alpha'
    },
    cloudWatch: {
      logGroupName: 'upgrade_medic-workflow-logs-alpha'
    }
  },
  beta: {
    accountId: '520852243958',
    region: 'us-west-2',
    roleArn: 'arn:aws:iam::520852243958:role/upgrade-medic-ui-role-beta',
    tables: {
      resourceInventory: 'verscender-resource-inventory-beta',
      jobRuns: 'verscender-job-runs-beta',
      versionSetDetails: 'verscender-versionset-details-beta'
    },
    cloudWatch: {
      logGroupName: 'upgrade_medic-workflow-logs-beta'
    }
  },
  prod: {
    accountId: '528460322591',
    region: 'us-west-2',
    roleArn: 'arn:aws:iam::528460322591:role/upgrade-medic-ui-role-prod',
    tables: {
      resourceInventory: 'verscender-resource-inventory-prod',
      jobRuns: 'verscender-job-runs-prod',
      versionSetDetails: 'verscender-versionset-details-prod'
    },
    cloudWatch: {
      logGroupName: 'upgrade_medic-workflow-logs-prod'
    }
  }
};

/** Resolve the build-time stage template variable. Falls back to 'alpha' in dev. */
const resolveStage = (): string => ('alpha'.includes('{{') ? 'alpha' : 'alpha'.toLowerCase());

/** Resolve the current stage config. Falls back to alpha. */
export const getStageConfig = (): UpgradeMedicStageConfig => {
  return STAGE_CONFIGS[resolveStage()] || STAGE_CONFIGS.alpha;
};

// WebSocket / local agent server
export const WEBSOCKET_URL = 'ws://localhost:8765';

/** Maps internal stage names to WebSocket payload stage names. Maps 'dev' to 'alpha'. */
export const getWebSocketStage = (): string => {
  const stage = resolveStage();
  return stage === 'dev' ? 'alpha' : stage;
};

export const INSTALL_COMMAND =
  'curl "https://code.amazon.com/packages/DaSOEVersionUpdaterService/blobs/mainline/--/run_upgrade_medic.sh?raw=1" -LSsf --location-trusted -u: -c ~/.midway/cookie -b ~/.midway/cookie | bash';

export const INSTALL_COMMAND_MAC_LOCAL =
  'curl "https://code.amazon.com/packages/DaSOEVersionUpdaterService/blobs/mainline/--/run_upgrade_medic.sh?raw=1" -LSsf --location-trusted -u: -c ~/.midway/cookie -b ~/.midway/cookie | bash -s -- --local';

export const SSH_TUNNEL_COMMAND = 'ssh -L 8765:localhost:8765 <your-cloud-desktop-hostname>';

// Default fallback region for resources without an explicit region
export const DEFAULT_RESOURCE_REGION = 'us-east-1';

// ============================================
// RESOURCE TYPE CONFIGURATION
// ============================================

export interface VersionOption {
  value: string;
  label: string;
  isRecommended?: boolean;
  isEOL?: boolean;
  eolDate?: string;
}

export interface ResourceTypeConfig {
  id: string;
  name: string;
  displayName: string;
  icon: string; // Emoji or icon name
  awsuiIcon?: IconProps.Name;
  description: string;
  enabled: boolean;
  comingSoon?: boolean;

  // Version configurations for this resource type
  versionCategories: {
    id: string;
    displayName: string;
    icon: string;
    versions: VersionOption[];
  }[];

  // Fields to display in the table
  tableColumns: {
    id: string;
    header: string;
    field: string;
    width?: number;
  }[];

  // Stats cards to show in dashboard
  statsCards: {
    id: string;
    label: string;
    filterKey: string;
    color?: string;
    countFn: (resources: any[]) => number;
  }[];

  // EOL runtime/version list for filtering
  eolVersions: string[];

  // Approaching EOL runtime/version list (will be EOL within 6-12 months)
  approachingEolVersions?: string[];
}

// ============================================
// LAMBDA CONFIGURATION
// ============================================

export const LAMBDA_CONFIG: ResourceTypeConfig = {
  id: 'lambda',
  name: 'Lambda',
  displayName: 'AWS Lambda Functions',
  icon: '',
  awsuiIcon: 'script',
  description: 'Update Lambda function runtime versions',
  enabled: true,

  versionCategories: [
    {
      id: 'python',
      displayName: 'Python',
      icon: '🐍',
      versions: [
        { value: 'python3.13', label: 'Python 3.13 (Latest)' },
        { value: 'python3.12', label: 'Python 3.12', isRecommended: true },
        { value: 'python3.11', label: 'Python 3.11' },
        { value: 'python3.10', label: 'Python 3.10' },
        { value: 'python3.9', label: 'Python 3.9 (EOL Dec 2025)', isEOL: true, eolDate: '2025-12-01' },
        { value: 'python3.8', label: 'Python 3.8 (EOL Oct 2024)', isEOL: true, eolDate: '2024-10-01' }
      ]
    },
    {
      id: 'nodejs',
      displayName: 'Node.js',
      icon: '🟢',
      versions: [
        { value: 'nodejs22.x', label: 'Node.js 22 (Latest)', isRecommended: true },
        { value: 'nodejs20.x', label: 'Node.js 20 LTS' },
        { value: 'nodejs18.x', label: 'Node.js 18 LTS' },
        { value: 'nodejs16.x', label: 'Node.js 16 (EOL)', isEOL: true, eolDate: '2024-06-01' },
        { value: 'nodejs14.x', label: 'Node.js 14 (EOL)', isEOL: true, eolDate: '2023-11-01' }
      ]
    },
    {
      id: 'java',
      displayName: 'Java',
      icon: '☕',
      versions: [
        { value: 'java21', label: 'Java 21 (Latest LTS)', isRecommended: true },
        { value: 'java17', label: 'Java 17 LTS' },
        { value: 'java11', label: 'Java 11 (EOL)', isEOL: true, eolDate: '2024-12-01' },
        { value: 'java8', label: 'Java 8 (EOL)', isEOL: true, eolDate: '2024-01-01' }
      ]
    }
  ],

  tableColumns: [
    { id: 'name', header: 'Function Name', field: 'name' },
    { id: 'type', header: 'Type', field: 'type', width: 100 },
    { id: 'runtime', header: 'Runtime', field: 'runtime', width: 120 },
    { id: 'status', header: 'Status', field: 'status', width: 100 },
    { id: 'region', header: 'Region', field: 'region', width: 120 }
  ],

  statsCards: [
    {
      id: 'total',
      label: 'Total Lambdas',
      filterKey: 'all',
      countFn: (resources) => resources.length
    },
    {
      id: 'eol',
      label: 'EOL Runtimes',
      filterKey: 'eol',
      color: '#f59e0b',
      countFn: (resources) =>
        resources.filter((r: any) => ['python3.8', 'python3.9', 'nodejs14.x', 'nodejs16.x', 'java8', 'java11'].includes(r.runtime)).length
    },
    {
      id: 'pipelines',
      label: 'Pipelines',
      filterKey: 'pipelines',
      color: '#3b82f6',
      countFn: (resources) => 0 // Calculated separately
    },
    {
      id: 'job-executions',
      label: 'Job Executions',
      filterKey: 'job-executions',
      color: '#10b981',
      countFn: () => -1 // Special value: handled separately for job count
    }
  ],

  eolVersions: ['python3.8', 'python3.9', 'nodejs14.x', 'nodejs16.x', 'nodejs18.x'],
  approachingEolVersions: ['python3.10', 'python3.11', 'nodejs20.x']
};

// ============================================
// POSTGRESQL RDS CONFIGURATION
// ============================================

export const POSTGRESQL_CONFIG: ResourceTypeConfig = {
  id: 'postgresql',
  name: 'Aurora PostgreSQL',
  displayName: 'RDS PostgreSQL',
  icon: '',
  awsuiIcon: 'add-plus',
  description: 'Update RDS PostgreSQL database versions',
  enabled: true,
  comingSoon: true,

  versionCategories: [
    {
      id: 'postgresql',
      displayName: 'PostgreSQL',
      icon: '🐘',
      versions: [
        { value: '16.4', label: 'PostgreSQL 16.4 (Latest)', isRecommended: true },
        { value: '16.3', label: 'PostgreSQL 16.3' },
        { value: '15.8', label: 'PostgreSQL 15.8' },
        { value: '15.7', label: 'PostgreSQL 15.7' },
        { value: '14.13', label: 'PostgreSQL 14.13' },
        { value: '13.16', label: 'PostgreSQL 13.16' },
        { value: '12.20', label: 'PostgreSQL 12.20 (EOL Feb 2025)', isEOL: true, eolDate: '2025-02-01' },
        { value: '11.22', label: 'PostgreSQL 11.22 (EOL)', isEOL: true, eolDate: '2024-02-01' }
      ]
    }
  ],

  tableColumns: [
    { id: 'name', header: 'Instance Name', field: 'name' },
    { id: 'class', header: 'Instance Class', field: 'instanceClass', width: 120 },
    { id: 'version', header: 'Version', field: 'version', width: 100 },
    { id: 'status', header: 'Status', field: 'status', width: 100 },
    { id: 'region', header: 'Region', field: 'region', width: 120 }
  ],

  statsCards: [
    {
      id: 'total',
      label: 'Total Instances',
      filterKey: 'all',
      countFn: (resources) => resources.length
    },
    {
      id: 'eol',
      label: 'EOL Versions',
      filterKey: 'eol',
      color: '#f59e0b',
      countFn: (resources) => resources.filter((r: any) => ['11.22', '12.20'].includes(r.version)).length
    },
    {
      id: 'in-progress',
      label: 'Upgrading',
      filterKey: 'in-progress',
      countFn: (resources) => resources.filter((r: any) => r.status === 'Upgrading').length
    },
    {
      id: 'accounts',
      label: 'Accounts',
      filterKey: 'accounts',
      color: '#3b82f6',
      countFn: (resources) => 0 // Calculated separately
    }
  ],

  eolVersions: ['11.22', '12.20']
};

// ============================================
// EMR CONFIGURATION (PLACEHOLDER)
// ============================================

export const EMR_CONFIG: ResourceTypeConfig = {
  id: 'emr',
  name: 'EMR',
  displayName: 'Amazon EMR',
  icon: '',
  awsuiIcon: 'status-pending',
  description: 'Update EMR cluster versions',
  enabled: true,
  comingSoon: true,

  versionCategories: [
    {
      id: 'emr',
      displayName: 'EMR Release',
      icon: '⚛️',
      versions: [
        { value: 'emr-7.2.0', label: 'EMR 7.2.0 (Latest)', isRecommended: true },
        { value: 'emr-7.1.0', label: 'EMR 7.1.0' },
        { value: 'emr-6.15.0', label: 'EMR 6.15.0' },
        { value: 'emr-6.14.0', label: 'EMR 6.14.0' },
        { value: 'emr-5.36.2', label: 'EMR 5.36.2 (EOL)', isEOL: true }
      ]
    }
  ],

  tableColumns: [
    { id: 'name', header: 'Cluster Name', field: 'name' },
    { id: 'version', header: 'EMR Version', field: 'version', width: 120 },
    { id: 'status', header: 'Status', field: 'status', width: 100 },
    { id: 'region', header: 'Region', field: 'region', width: 120 }
  ],

  statsCards: [
    { id: 'total', label: 'Total Clusters', filterKey: 'all', countFn: (r) => r.length },
    { id: 'eol', label: 'EOL Versions', filterKey: 'eol', color: '#f59e0b', countFn: (r) => r.filter((x: any) => x.version?.includes('5.')).length },
    { id: 'in-progress', label: 'Upgrading', filterKey: 'in-progress', countFn: (r) => r.filter((x: any) => x.status === 'Upgrading').length },
    { id: 'accounts', label: 'Accounts', filterKey: 'accounts', color: '#3b82f6', countFn: () => 0 }
  ],

  eolVersions: ['emr-5.36.2', 'emr-5.35.0']
};

// ============================================
// ELASTICSEARCH/OPENSEARCH CONFIGURATION (PLACEHOLDER)
// ============================================

export const OPENSEARCH_CONFIG: ResourceTypeConfig = {
  id: 'opensearch',
  name: 'OpenSearch',
  displayName: 'Amazon OpenSearch',
  icon: '🔍',
  description: 'Update OpenSearch domain versions',
  enabled: false,
  comingSoon: true,

  versionCategories: [
    {
      id: 'opensearch',
      displayName: 'OpenSearch',
      icon: '🔍',
      versions: [
        { value: '2.11', label: 'OpenSearch 2.11 (Latest)', isRecommended: true },
        { value: '2.9', label: 'OpenSearch 2.9' },
        { value: '1.3', label: 'OpenSearch 1.3' }
      ]
    }
  ],

  tableColumns: [
    { id: 'name', header: 'Domain Name', field: 'name' },
    { id: 'version', header: 'Version', field: 'version', width: 100 },
    { id: 'status', header: 'Status', field: 'status', width: 100 }
  ],

  statsCards: [{ id: 'total', label: 'Total Domains', filterKey: 'all', countFn: (r) => r.length }],

  eolVersions: []
};

// ============================================
// MASTER CONFIGURATION REGISTRY
// ============================================

export const RESOURCE_CONFIGS: ResourceTypeConfig[] = [LAMBDA_CONFIG];

// Get enabled configs only
export const getEnabledConfigs = () => RESOURCE_CONFIGS.filter((c) => c.enabled);

// Get config by ID
export const getConfigById = (id: string) => RESOURCE_CONFIGS.find((c) => c.id === id);

// ============================================
// UTILITY FUNCTIONS
// ============================================

export const getVersionCategoryByRuntime = (config: ResourceTypeConfig, runtime: string) => {
  for (const category of config.versionCategories) {
    if (category.versions.some((v) => v.value === runtime)) {
      return category;
    }
  }
  return null;
};

export const isEOLVersion = (config: ResourceTypeConfig, version: string) => {
  return config.eolVersions.includes(version);
};

// ============================================
// RUNTIME CONFIGS FOR VERSION SELECTION MODAL
// ============================================

import { RuntimeConfig } from './types';

export const RUNTIME_CONFIGS: RuntimeConfig[] = [
  {
    language: 'python',
    displayName: 'Python',
    icon: '🐍',
    currentRuntimes: ['python3.8', 'python3.9', 'python3.10', 'python3.11', 'python3.12', 'python3.13', 'python3.14'],
    versions: [
      { value: 'python3.10', label: 'Python 3.10 (Approaching EOL)' },
      { value: 'python3.11', label: 'Python 3.11 (Approaching EOL)' },
      { value: 'python3.12', label: 'Python 3.12 (Recommended)', isRecommended: true },
      { value: 'python3.13', label: 'Python 3.13' },
      { value: 'python3.14', label: 'Python 3.14 (Latest)', isLatest: true }
    ]
  },
  {
    language: 'java',
    displayName: 'Java',
    icon: '☕',
    currentRuntimes: ['java8', 'java8.al2', 'java11', 'java17', 'java21', 'java25'],
    versions: [
      { value: 'java8', label: 'Java 8' },
      { value: 'java11', label: 'Java 11' },
      { value: 'java17', label: 'Java 17 (Corretto)' },
      { value: 'java21', label: 'Java 21 (Corretto - Recommended)', isRecommended: true },
      { value: 'java25', label: 'Java 25', isRecommended: true }
    ]
  },
  {
    language: 'nodejs',
    displayName: 'Node.js',
    icon: '📦',
    currentRuntimes: ['nodejs14.x', 'nodejs16.x', 'nodejs18.x', 'nodejs20.x'],
    versions: [
      { value: 'nodejs20.x', label: 'Node.js 20.x (Approaching EOL)' },
      { value: 'nodejs22.x', label: 'Node.js 22.x (Recommended)', isRecommended: true },
      { value: 'nodejs24.x', label: 'Node.js 24.x' }
    ]
  },
  {
    language: 'dotnet',
    displayName: '.NET',
    icon: '🔷',
    currentRuntimes: ['dotnet6', 'dotnet8'],
    versions: [
      { value: 'dotnet6', label: '.NET 6' },
      { value: 'dotnet8', label: '.NET 8 (Recommended)', isRecommended: true }
    ]
  },
  {
    language: 'go',
    displayName: 'Go',
    icon: '🔵',
    currentRuntimes: ['go1.x', 'provided.al2', 'provided.al2023'],
    versions: [{ value: 'provided.al2023', label: 'Go (AL2023 - Recommended)', isRecommended: true }]
  },
  {
    language: 'ruby',
    displayName: 'Ruby',
    icon: '💎',
    currentRuntimes: ['ruby2.7', 'ruby3.2', 'ruby3.3'],
    versions: [
      { value: 'ruby3.2', label: 'Ruby 3.2' },
      { value: 'ruby3.3', label: 'Ruby 3.3 (Recommended)', isRecommended: true }
    ]
  }
];

// Helper function to get language from runtime string
export const getLanguageFromRuntime = (runtime: string): RuntimeConfig | undefined => {
  const runtimeLower = runtime.toLowerCase();

  // Check each config to see if the runtime matches the language pattern
  return RUNTIME_CONFIGS.find((config) => {
    switch (config.language) {
      case 'python':
        return runtimeLower.startsWith('python');
      case 'nodejs':
        return runtimeLower.startsWith('nodejs') || runtimeLower.startsWith('node');
      case 'java':
        return runtimeLower.startsWith('java');
      case 'dotnet':
        return runtimeLower.startsWith('dotnet') || runtimeLower.includes('.net');
      case 'go':
        return runtimeLower.startsWith('go') || runtimeLower.startsWith('provided');
      case 'ruby':
        return runtimeLower.startsWith('ruby');
      default:
        return false;
    }
  });
};

// Helper to get count of lambdas by language
export const getLambdaCountByLanguage = (lambdas: { runtime: string }[]): Record<string, number> => {
  const counts: Record<string, number> = {};

  lambdas.forEach((lambda) => {
    const config = getLanguageFromRuntime(lambda.runtime);
    if (config) {
      counts[config.language] = (counts[config.language] || 0) + 1;
    }
  });

  return counts;
};
